import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * B2C currency context. Powers the currency picker in the navbar.
 *
 * All prices in the app are kept in USD internally — Stripe still charges USD,
 * the booking record stores USD, suppliers receive their native amounts. This
 * context only changes the *display* layer.
 *
 * Every conversion uses the same admin-managed rate book served by
 * `/api/fx/public-rates` so what the customer sees ALWAYS matches what
 * /admin/fx shows + admin's FX margin %.
 *
 * Usage:
 *   const { format, convert, currency, setCurrency } = useCurrency();
 *   <span>{format(88.13)}</span>   // → "AED 323.62" with margin baked in
 */

const CurrencyContext = createContext({
  currency: "USD",
  setCurrency: () => {},
  rates: [],
  rateMap: { USD: { code: "USD", symbol: "$", usd_to_unit: 1, effective_rate_to_usd: 1, margin_pct: 0 } },
  convert: (usd) => Number(usd) || 0,
  format: (usd) => `$${(Number(usd) || 0).toFixed(2)}`,
  loading: true,
});

const STORAGE_KEY = "fa_display_ccy";

// Currencies we surface in the picker by default. Anything else the admin has
// loaded is still available via the FULL list inside the picker — these just
// show on top for fast access.
const POPULAR_TOP = ["USD", "EUR", "GBP", "AED", "SAR", "EGP", "INR", "JPY", "CAD", "AUD"];

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "USD"; } catch { return "USD"; }
  });
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Persist the user's pick — sticky across sessions
  const setCurrency = useCallback((code) => {
    const c = (code || "USD").toUpperCase();
    setCurrencyState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/fx/public-rates");
        if (!alive) return;
        const arr = data?.rates || [];
        setRates(arr);
        // If the user previously picked a currency that's no longer loaded
        // (e.g. admin removed it), fall back to USD silently.
        const codes = new Set(arr.map((r) => r.code));
        if (!codes.has(currency)) setCurrency("USD");
      } catch (_e) {
        // FX feed not loaded yet (admin hasn't clicked Refresh). Keep USD.
        if (alive) setRates([{ code: "USD", symbol: "$", name: "US Dollar",
                               usd_to_unit: 1, effective_rate_to_usd: 1,
                               rate_to_usd: 1, margin_pct: 0 }]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rateMap = useMemo(() => {
    const m = {};
    for (const r of rates) m[r.code] = r;
    if (!m.USD) m.USD = { code: "USD", symbol: "$", usd_to_unit: 1, effective_rate_to_usd: 1, margin_pct: 0 };
    return m;
  }, [rates]);

  const convert = useCallback((amountUsd) => {
    const n = Number(amountUsd) || 0;
    const r = rateMap[currency];
    if (!r || currency === "USD" || !r.usd_to_unit) return n;
    return n * Number(r.usd_to_unit);
  }, [currency, rateMap]);

  const format = useCallback((amountUsd, opts = {}) => {
    const n = Number(amountUsd) || 0;
    const r = rateMap[currency];
    if (!r || currency === "USD" || !r.usd_to_unit) {
      return `$${n.toFixed(opts.decimals ?? 2)}`;
    }
    const v = n * Number(r.usd_to_unit);
    // Use Intl for big numbers — fall back to manual fixed for tiny ones
    try {
      const decimals = opts.decimals ?? (v >= 100 ? 0 : 2);
      const formatted = v.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      // Symbol-prefix layout: "AED 4,237", "$88", "€80"
      const sym = r.symbol || r.code;
      // Glyph symbols ($, €, £, ¥, ₹) hug the number; alpha codes get a space
      const isGlyph = sym.length <= 2 && !/[A-Z]{2,}/.test(sym);
      return isGlyph ? `${sym}${formatted}` : `${sym} ${formatted}`;
    } catch (_e) {
      return `${r.symbol || r.code} ${v.toFixed(2)}`;
    }
  }, [currency, rateMap]);

  const value = useMemo(() => ({
    currency, setCurrency, rates, rateMap, convert, format, loading,
    popular: POPULAR_TOP,
  }), [currency, setCurrency, rates, rateMap, convert, format, loading]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() { return useContext(CurrencyContext); }
